/**
 * The query key factory. One place, so an invalidation cannot miss a cache.
 */
export const queryKeys = {
  me: () => ['me'] as const,

  live: {
    facility: () => ['live', 'facility'] as const,
    zones: () => ['live', 'zones'] as const,
    bays: () => ['live', 'bays'] as const,
  },

  zones: {
    tree: () => ['zones', 'tree'] as const,
    flat: () => ['zones', 'flat'] as const,
    occupancy: (params: Record<string, unknown>) =>
      ['analytics', 'zone-occupancy', params] as const,
  },

  /** Feature 027. One factory entry per endpoint, so a settings write can invalidate exactly the staffing read. */
  analytics: {
    forecast: (scope: string, quantity: string) =>
      ['analytics', 'forecast', scope, quantity] as const,
    readiness: (scope: string) => ['analytics', 'forecast-readiness', scope] as const,
    reviews: (scope: string) => ['analytics', 'forecast-reviews', scope] as const,
    anomalies: () => ['analytics', 'anomalies'] as const,
    valetStaffing: () => ['analytics', 'valet-staffing'] as const,
    flow: (params: Record<string, unknown>) => ['analytics', 'zone-flow', params] as const,
  },

  bays: {
    list: (params: Record<string, unknown>) => ['bays', 'list', params] as const,
    /** The plan geometry. Configuration, cached hard — see useMapLayout. */
    layout: () => ['bays', 'layout'] as const,
    detail: (code: string) => ['bays', 'detail', code] as const,
    device: (code: string) => ['bays', 'device', code] as const,
    calibration: (code: string) => ['bays', 'calibration', code] as const,
    history: (code: string, params: Record<string, unknown>) =>
      ['bays', 'history', code, params] as const,
    telemetry: (code: string, params: Record<string, unknown>) =>
      ['bays', 'telemetry', code, params] as const,
  },

  devices: {
    list: (params: Record<string, unknown>) => ['devices', 'list', params] as const,
    detail: (devEui: string) => ['devices', 'detail', devEui] as const,
    commissioningProgress: () => ['commissioning', 'progress'] as const,
  },

  calibration: {
    proposals: (params: Record<string, unknown>) => ['calibration', 'proposals', params] as const,
  },

  alerts: {
    list: (params: Record<string, unknown>) => ['alerts', 'list', params] as const,
    detail: (id: string) => ['alerts', 'detail', id] as const,
    rules: () => ['alert-rules'] as const,
    ruleTest: (id: string) => ['alert-rules', id, 'test'] as const,
    notificationGroups: () => ['notification-groups'] as const,
  },

  reports: {
    catalogue: () => ['reports', 'catalogue'] as const,
    runs: (params: Record<string, unknown>) => ['reports', 'runs', params] as const,
    run: (id: string) => ['reports', 'run', id] as const,
    schedules: () => ['report-schedules'] as const,
    exports: (params: Record<string, unknown>) => ['exports', params] as const,
  },

  health: {
    summary: () => ['health', 'summary'] as const,
    devices: (params: Record<string, unknown>) => ['health', 'devices', params] as const,
    gateways: (params: Record<string, unknown>) => ['health', 'gateways', params] as const,
    sensorReadings: (devEui: string, page: number) =>
      ['health', 'sensor-readings', devEui, page] as const,
    pipeline: () => ['health', 'pipeline'] as const,
    failedPayloads: (params: Record<string, unknown>) =>
      ['health', 'failed-payloads', params] as const,
  },

  valet: {
    sessions: (params: Record<string, unknown>) => ['valet', 'sessions', params] as const,
  },

  admin: {
    users: () => ['users'] as const,
    roles: () => ['roles'] as const,
    audit: (params: Record<string, unknown>) => ['audit', params] as const,
    signs: () => ['signs'] as const,
    displays: () => ['displays'] as const,
    displayProfile: (id: string) => ['displays', id, 'profile'] as const,
    displayPreview: (id: string) => ['displays', id, 'preview'] as const,
    displayMessage: (id: string) => ['displays', id, 'message'] as const,
    signPreview: (code: string) => ['signs', code, 'preview'] as const,
    signTemplates: () => ['sign-templates'] as const,
    apiKeys: () => ['api-keys'] as const,
    webhooks: () => ['webhooks'] as const,
    notes: (params: Record<string, unknown>) => ['notes', params] as const,
    holds: () => ['maintenance-holds'] as const,
  },
} as const;
